import React, { useEffect } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, MessageCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { NewPageFormState } from "../types";
import { useClarifyingQuestions } from "@/hooks/content-generator/pages/use-clarifying-questions";
import { usePageCreationResearch } from "@/hooks/universal-creation-research";

interface ClarifyingQuestionsStepProps {
  value: NewPageFormState;
  onChange: (value: NewPageFormState) => void;
  goalThreadId: string;
}

export const ClarifyingQuestionsStep: React.FC<ClarifyingQuestionsStepProps> = ({
  value,
  onChange,
  goalThreadId,
}) => {
  const { selectedResearch, summaryData } = usePageCreationResearch();
  
  const {
    questions,
    answers,
    updateAnswer,
    generateQuestions,
    resetQuestions,
    isGenerating,
    error
  } = useClarifyingQuestions(goalThreadId);

  // Generate questions when step loads - only once per form state change
  useEffect(() => {
    if (!questions.length && !isGenerating && value.title && value.initial_request && value.type) {
      const researchSummaries = selectedResearch.map(research => ({
        title: research.research_library.title,
        summary: summaryData[research.id] || research.research_library.content?.substring(0, 200) || ''
      }));
      
      generateQuestions(value, researchSummaries);
    }
  }, [
    value.title, 
    value.initial_request, 
    value.type, 
    selectedResearch, 
    summaryData, 
    generateQuestions, 
    questions.length, 
    isGenerating
  ]); // Include all dependencies used in the effect

  const handleAnswerChange = (index: number, answer: string) => {
    updateAnswer(index, answer);
    
    // Update form state immediately when answers change
    onChange({
      ...value,
      clarifyingQuestions: {
        questions,
        answers: answers.map((a, i) => i === index ? answer : a)
      }
    });
  };

  const handleRegenerateQuestions = () => {
    resetQuestions();
    const researchSummaries = selectedResearch.map(research => ({
      title: research.research_library.title,
      summary: summaryData[research.id] || research.research_library.content?.substring(0, 200) || ''
    }));
    generateQuestions(value, researchSummaries);
  };

  // Get count of answered questions (minimum 30 characters for meaningful answer)
  const answeredCount = answers.filter(answer => answer.length >= 30).length;

  if (isGenerating) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center py-12 space-y-4"
      >
        <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full">
          <Loader2 size={32} className="text-purple-600 dark:text-purple-400 animate-spin" />
        </div>
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-foreground">Generating Personalized Questions</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            The AI is analyzing your document details to create 2 targeted questions that will help generate better, more relevant content.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Questions and Answers */}
      <div className="space-y-6">
        {questions.map((question, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="space-y-3"
          >
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">
                  {index + 1}
                </span>
              </div>
              <div className="flex-1 space-y-2">
                <Label className="text-sm font-medium text-foreground leading-relaxed">
                  {question}
                </Label>
                <Textarea
                  value={answers[index]}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  placeholder="Your detailed answer will help create better, more targeted content..."
                  rows={3}
                  className="resize-none transition-all duration-200"
                />
                <div className="flex justify-between items-center text-xs">
                  <div className="text-muted-foreground">
                    {answers[index].length >= 30 ? (
                      <span className="text-green-600 dark:text-green-400">✓ Good detail level</span>
                    ) : answers[index].length > 0 ? (
                      <span className="text-amber-600 dark:text-amber-400">
                        {30 - answers[index].length} more characters for better results
                      </span>
                    ) : (
                      <span className="text-muted-foreground"></span>
                    )}
                  </div>
                  <div className="text-muted-foreground font-mono">
                    {answers[index].length} chars
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}; 